// A Komoot GPX, measured the way the route library always has been: distance by haversine,
// climb from heights smoothed over nine points, the steepest sustained 500 m, a 160-point height
// profile and a simplified line for the map. Pure: it runs in the app and in the tests.

export type Pt = [number, number, number | null]; // lat, lon, ele
export type Measured = {
  name: string;
  km: number;
  gain: number;
  steep: number;
  loop: boolean;        // ends within 1 km of where it starts
  startKmFromHome: number | null;
  profile: [number, number][];
  track: [number, number][];
  pts: number;
};

export function parseGpx(xml: string): { name: string; pts: Pt[] } {
  const name = /<trk>\s*<name>([^<]*)<\/name>/.exec(xml)?.[1] ?? /<name>([^<]*)<\/name>/.exec(xml)?.[1] ?? "New route";
  const pts: Pt[] = [];
  const re = /<(?:trkpt|rtept)\s+([^>]*?)\/?>([\s\S]*?)(?:<\/(?:trkpt|rtept)>|(?=<(?:trkpt|rtept)\s)|$)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml))) {
    const lat = /lat="([-\d.]+)"/.exec(m[1])?.[1], lon = /lon="([-\d.]+)"/.exec(m[1])?.[1];
    if (!lat || !lon) continue;
    const ele = /<ele>([-\d.]+)<\/ele>/.exec(m[2] ?? "")?.[1];
    pts.push([Number(lat), Number(lon), ele != null ? Number(ele) : null]);
  }
  return { name: decode(name.trim()), pts };
}
const decode = (s: string) => s.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;|&apos;/g, "'");

export const hav = (a: [number, number] | Pt, b: [number, number] | Pt) => {
  const R = 6371, r = Math.PI / 180, dp = (b[0] - a[0]) * r, dl = (b[1] - a[1]) * r;
  return 2 * R * Math.asin(Math.sqrt(Math.sin(dp / 2) ** 2 + Math.cos(a[0] * r) * Math.cos(b[0] * r) * Math.sin(dl / 2) ** 2));
};

/** Douglas-Peucker in a local flat projection; eps in degrees of latitude (0.0006 is about 65 m). */
export function simplify(pts: [number, number][], eps = 0.0006): [number, number][] {
  if (pts.length < 3) return pts;
  const k = Math.cos((pts[0][0] * Math.PI) / 180);
  const keep = new Uint8Array(pts.length); keep[0] = keep[pts.length - 1] = 1;
  const stack: [number, number][] = [[0, pts.length - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    const [ay, ax] = [pts[a][0], pts[a][1] * k], [by, bx] = [pts[b][0], pts[b][1] * k];
    const dx = bx - ax, dy = by - ay, L = dx * dx + dy * dy;
    let best = -1, bd = 0;
    for (let i = a + 1; i < b; i++) {
      const px = pts[i][1] * k, py = pts[i][0];
      const t = L ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / L)) : 0;
      const d = Math.hypot(px - ax - t * dx, py - ay - t * dy);
      if (d > bd) { bd = d; best = i; }
    }
    if (best > 0 && bd > eps) { keep[best] = 1; stack.push([a, best], [best, b]); }
  }
  return pts.filter((_, i) => keep[i]).map((p) => [Math.round(p[0] * 1e4) / 1e4, Math.round(p[1] * 1e4) / 1e4]);
}

export function measure(name: string, pts: Pt[], home?: { lat: number; lon: number }): Measured {
  const cum = [0];
  for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1] + hav(pts[i - 1], pts[i]));
  const km = cum[cum.length - 1] ?? 0;
  // Heights: fill gaps from the last known height, then a nine-point moving average.
  let last = pts.find((p) => p[2] != null)?.[2] ?? 0;
  const raw = pts.map((p) => (p[2] != null ? (last = p[2]) : last));
  const sm = raw.map((_, i) => { let s = 0, n = 0; for (let j = Math.max(0, i - 4); j <= Math.min(raw.length - 1, i + 4); j++) { s += raw[j]; n++; } return s / n; });
  let gain = 0;
  for (let i = 1; i < sm.length; i++) if (sm[i] > sm[i - 1]) gain += sm[i] - sm[i - 1];
  // Steepest sustained 500 m.
  let steep = 0;
  for (let i = 0, j = 0; i < sm.length; i++) {
    while (j < sm.length - 1 && cum[j] - cum[i] < 0.5) j++;
    if (cum[j] - cum[i] >= 0.5) steep = Math.max(steep, ((sm[j] - sm[i]) / ((cum[j] - cum[i]) * 1000)) * 100);
  }
  const profile: [number, number][] = [];
  for (let k = 0, j = 0; k <= 160; k++) {
    const t = (km * k) / 160;
    while (j < cum.length - 1 && cum[j + 1] < t) j++;
    profile.push([Math.round(t * 10) / 10, Math.round(sm[j] ?? 0)]);
  }
  const ends = pts.length > 1 ? hav(pts[0], pts[pts.length - 1]) : 0;
  return {
    name, km: Math.round(km * 10) / 10, gain: Math.round(gain), steep: Math.round(steep * 10) / 10,
    loop: ends < 1, startKmFromHome: home && pts.length ? Math.round(hav([home.lat, home.lon], pts[0]) * 10) / 10 : null,
    profile, track: simplify(pts.map((p) => [p[0], p[1]] as [number, number])), pts: pts.length,
  };
}

/** A route id from its name: "Werneth Low (Long)" becomes "werneth-low-long". */
export const slug = (s: string) => s.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "route";
